import React, { useState, useEffect, useMemo, Component } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import '../../Assets/Style/style.css';
import LeaseApiService from '../../services/apiService';
import { RiRefreshLine } from 'react-icons/ri';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import { FaPrint } from 'react-icons/fa';
import { IoMailOutline } from 'react-icons/io5';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { useCurrency } from '../../../INVENTORY/Layouts/currencyContext';
import ColumnFilterHeader from './columnFilterHeader';

/* ---------------------------------------------------------------- */
/* Helpers                                                            */
/* ---------------------------------------------------------------- */

const selectTheme = (theme) => ({ ...theme, borderRadius: 5, padding: 9 });
const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
    color: state.isFocused ? '#000 ' : '#000',
    ':hover': { backgroundColor: '#F5F5F5', color: '#000 ' },
  }),
};

const emptyValueFormatter = (params) => {
  const v = params.value;
  if (v === null || v === undefined || v === '' || v === '0') return '--';
  return v;
};

const dateFormatter = (params) => {
  if (!params.value) return '--';
  const date = moment(params.value);
  return date.isValid() ? date.format('DD-MMM-YYYY') : '--';
};

const dateComparator = (date1, date2) => {
  const momentDate1 = moment(date1);
  const momentDate2 = moment(date2);
  return momentDate1.isSame(momentDate2) ? 0 : momentDate1.isBefore(momentDate2) ? -1 : 1;
};

const mapInvoiceRow = (item) => ({
  key: [item?.invoiceNo, item?.leaseNo, item?.tranId].filter(Boolean).join('|') || `invoice-${Math.random().toString(36).slice(2)}`,
  tranId: item?.tranId,
  groupId: item?.groupId,
  leaseNo: item?.leaseNo || '',
  leaseStart: item?.leaseStart || '',
  leaseEnd: item?.leaseEnd || '',
  invoiceNo: item?.invoiceNo || '',
  invoiceDate: item?.invoiceDate || '',
  narration: item?.description || '',
  tenantName: item?.tenantName || '',
  propertyName: item?.propertyName || '',
  unitNo: item?.unitNo || '',
  amount: item?.amount,
  email: '',
  tenantEmailId: '',
});

const buildMailToLink = (recipients) => {
  const uniqueRecipients = Array.from(new Set(recipients.map((r) => r.trim()).filter(Boolean)));
  if (uniqueRecipients.length === 0) return '';
  const subject = encodeURIComponent('Installment Invoice List');
  return `mailto:${uniqueRecipients.join(',')}?subject=${subject}`;
};

const buildInvoiceEmailLink = (row) => {
  const recipient = (row.tenantEmailId || '').trim() || (row.email || '').trim();
  if (!recipient) return '';
  const subject = encodeURIComponent(`Invoice ${row.invoiceNo || row.leaseNo || ''}`.trim());
  const body = encodeURIComponent(
    `Dear ${row.tenantName || 'Tenant'},\n\nPlease find attached invoice ${row.invoiceNo || '-'} dated ${row.invoiceDate || '-'} for lease ${row.leaseNo || '-'}, amount ${row.amount ?? '-'}.\n\nRegards.`,
  );
  return `mailto:${recipient}?subject=${subject}&body=${body}`;
};

const buildInvoicePrintHtml = (row) => {
  const fields = [
    ['Invoice No', row.invoiceNo || '-'],
    ['Invoice Date', row.invoiceDate || '-'],
    ['Lease No', row.leaseNo || '-'],
    ['Lease Start', row.leaseStart || '-'],
    ['Lease End', row.leaseEnd || '-'],
    ['Tenant Name', row.tenantName || '-'],
    ['Property Name', row.propertyName || '-'],
    ['Unit No', row.unitNo || '-'],
    ['Narration', row.narration || '-'],
    ['Amount', row.amount ?? '-'],
    ['Tenant Email', row.tenantEmailId || row.email || '-'],
  ];
  const rowsHtml = fields.map(([label, value]) => `<tr><th>${label}</th><td>${value}</td></tr>`).join('');
  return `<!DOCTYPE html><html><head><title>Invoice ${row.invoiceNo || ''}</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 24px; color: #1f2937; }
      h1 { font-size: 18px; margin-bottom: 16px; }
      table { border-collapse: collapse; width: 100%; max-width: 480px; }
      th, td { border: 1px solid #d1d5db; padding: 8px 12px; text-align: left; font-size: 13px; }
      th { background: #f3f4f6; width: 40%; }
    </style>
  </head><body>
    <h1>Installment Invoice</h1>
    <table>${rowsHtml}</table>
  </body></html>`;
};

const blankFormData = () => ({
  propertyNo: { value: 'All', label: 'All' },
  unitNo: null,
  fromDate: moment().startOf('month').toDate(),
  leaseExpiry: moment().endOf('month').toDate(),
});

/* ---------------------------------------------------------------- */
/* Component                                                          */
/* ---------------------------------------------------------------- */

const InstallmentInvoiceEmailListing = () => {
  const apiService = new LeaseApiService();
  const { currencyPrecision } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [rowData, setRowData] = useState([]);
  const [FilterTab, setFilterTab] = useState(true);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sendOnPrintKeys, setSendOnPrintKeys] = useState(new Set());
  const [gridApi, setGridApi] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});
  const [formData, setFormData] = useState(blankFormData());

  useEffect(() => {
    fetchPropertyOptions();
    getItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPropertyOptions = async () => {
    try {
      const params = new URLSearchParams();
      params.append('dropdownTypes', 'ReceiptPropertyNos');
      const res = await apiService.getApiParamater('LEASE_RECEIPT_MULTIDROP', `?${params.toString()}`);
      const items = res?.data?.dropdowns?.PropertyNo || [];
      const options = items.map((item) => ({ value: item?.value, label: item?.text }));
      setPropertyOptions([{ value: 'All', label: 'All' }, ...options]);
    } catch (error) {
      console.error('Error loading property dropdown:', error);
      setPropertyOptions([{ value: 'All', label: 'All' }]);
    }
  };

  const fetchUnitOptions = async (propertyNo) => {
    if (!propertyNo || propertyNo === 'All') {
      setUnitOptions([]);
      return;
    }
    setLoadingUnits(true);
    try {
      const params = new URLSearchParams();
      params.append('DropdownFor', 'LRSearchUnitNosByPropertyId');
      params.append('CascadValue', propertyNo);
      const res = await apiService.getApiParamater('LEASE_RECEIPT_CASCAD', `?${params.toString()}`);
      const items = res?.data?.items || [];
      setUnitOptions(items.map((item) => ({ value: item?.value, label: item?.text })));
    } catch (error) {
      console.error('Error loading unit dropdown:', error);
      setUnitOptions([]);
    } finally {
      setLoadingUnits(false);
    }
  };

  const handlePropertyChange = (selectedOption) => {
    setFormData((prev) => ({ ...prev, propertyNo: selectedOption, unitNo: null }));
    fetchUnitOptions(selectedOption?.value);
  };

  const enrichWithEmails = async (rows) => {
    const uniqueLeaseNos = Array.from(new Set(rows.map((r) => r.leaseNo).filter(Boolean)));
    if (!uniqueLeaseNos.length) return rows;

    const emailByLease = {};
    await Promise.all(
      uniqueLeaseNos.map(async (leaseNo) => {
        try {
          const params = new URLSearchParams();
          params.append('Action', 'GetCustomerDetailsByLeaseNo');
          params.append('LeaseNo', leaseNo);
          const res = await apiService.getApiParamater('LEASE_RECEIPT_COMMON', `?${params.toString()}`);
          emailByLease[leaseNo] = res?.data?.result?.data?.EmailId || '';
        } catch (error) {
          console.error(`Error loading tenant email for lease ${leaseNo}:`, error);
          emailByLease[leaseNo] = '';
        }
      }),
    );

    return rows.map((row) => ({
      ...row,
      email: emailByLease[row.leaseNo] || '',
      tenantEmailId: emailByLease[row.leaseNo] || '',
    }));
  };

  const getItems = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('PageNo', '1');
      params.append('PageSize', '50');
      params.append('PropertyNo', formData.propertyNo?.value || 'All');
      params.append('UnitNo', formData.unitNo?.label || 'All');
      params.append('Mode', 'EDIT');
      params.append('TenantNo', 'All');
      params.append('LeaseNo', 'All');
      params.append('UnitType', 'All');
      params.append('FromDate', formData.fromDate ? moment(formData.fromDate).format('YYYY-MM-DD') : 'All');
      params.append('ToDate', formData.leaseExpiry ? moment(formData.leaseExpiry).format('YYYY-MM-DD') : 'All');
      const res = await apiService.getApiParamater('GET_INSTALLMENT_INVOICE', `?${params.toString()}`);
      const rows = (res?.data?.result || []).filter((item) => item?.tranId !== 0).map(mapInvoiceRow);
      setRowData(rows);
      setSelectedRows([]);
      setLoading(false);

      const enrichedRows = await enrichWithEmails(rows);
      setRowData(enrichedRows);
    } catch (error) {
      console.error('Error fetching installment invoices:', error);
      setRowData([]);
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFormData(blankFormData());
    setUnitOptions([]);
    setColumnFilters({});
    setTimeout(() => getItems(), 0);
  };

  const toggleSendOnPrint = (key, checked) => {
    setSendOnPrintKeys((current) => {
      const next = new Set(current);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const printInvoice = (row) => {
    const printWindow = window.open('', '_blank', 'width=800,height=900');
    if (printWindow) {
      printWindow.document.write(buildInvoicePrintHtml(row));
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
    if (sendOnPrintKeys.has(row.key)) {
      const mailLink = buildInvoiceEmailLink(row);
      if (mailLink) {
        window.location.href = mailLink;
      }
    }
  };

  const sendSelectedEmails = () => {
    const emails = selectedRows.map((row) => row.tenantEmailId?.trim() || row.email?.trim()).filter(Boolean);
    const mailToLink = buildMailToLink(emails);
    if (!mailToLink) {
      alert('Select at least one row with an email address before sending.');
      return;
    }
    window.location.href = mailToLink;
  };

  const onSelectionChanged = () => {
    if (gridApi) {
      setSelectedRows(gridApi.getSelectedRows());
    }
  };

  const displayValue = (row, field, formatter) => (formatter ? formatter({ value: row?.[field] }) : emptyValueFormatter({ value: row?.[field] }));
  const buildFilterParams = (field, formatter) => ({
    getValues: () => rowData.map((r) => displayValue(r, field, formatter)),
    selected: columnFilters[field],
    onChange: (vals) => setColumnFilters((prev) => ({ ...prev, [field]: vals })),
    onClear: () =>
      setColumnFilters((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      }),
  });

  const filteredRowData = useMemo(() => {
    const activeFields = Object.keys(columnFilters).filter((f) => columnFilters[f] && columnFilters[f].length);
    if (!activeFields.length) return rowData;
    return rowData.filter((row) =>
      activeFields.every((field) => {
        const formatter = ['leaseStart', 'leaseEnd', 'invoiceDate'].includes(field) ? dateFormatter : undefined;
        return columnFilters[field].includes(displayValue(row, field, formatter));
      }),
    );
  }, [rowData, columnFilters]);

  const columnDefs = [
    {
      headerName: '',
      field: 'select',
      headerCheckboxSelection: true,
      checkboxSelection: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      width: 44,
      pinned: 'left',
    },
    {
      headerName: 'Lease No',
      field: 'leaseNo',
      suppressMovable: true,
      sortable: true,
      width: 130,
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('leaseNo'),
    },
    {
      headerName: 'Lease Start',
      field: 'leaseStart',
      suppressMovable: true,
      sortable: true,
      width: 120,
      valueFormatter: dateFormatter,
      comparator: dateComparator,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('leaseStart', dateFormatter),
    },
    {
      headerName: 'Lease End',
      field: 'leaseEnd',
      suppressMovable: true,
      sortable: true,
      width: 120,
      valueFormatter: dateFormatter,
      comparator: dateComparator,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('leaseEnd', dateFormatter),
    },
    {
      headerName: 'Invoice No',
      field: 'invoiceNo',
      suppressMovable: true,
      sortable: true,
      width: 130,
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('invoiceNo'),
    },
    {
      headerName: 'Invoice Date',
      field: 'invoiceDate',
      suppressMovable: true,
      sortable: true,
      width: 120,
      valueFormatter: dateFormatter,
      comparator: dateComparator,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('invoiceDate', dateFormatter),
    },
    {
      headerName: 'Narration',
      field: 'narration',
      suppressMovable: true,
      sortable: true,
      width: 200,
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('narration'),
    },
    {
      headerName: 'Tenant Name',
      field: 'tenantName',
      suppressMovable: true,
      sortable: true,
      width: 200,
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('tenantName'),
    },
    {
      headerName: 'Property Name',
      field: 'propertyName',
      suppressMovable: true,
      sortable: true,
      width: 200,
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('propertyName'),
    },
    {
      headerName: 'Unit No',
      field: 'unitNo',
      suppressMovable: true,
      sortable: true,
      width: 110,
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('unitNo'),
    },
    {
      headerName: 'Amount',
      field: 'amount',
      suppressMovable: true,
      sortable: true,
      width: 130,
      valueFormatter: (params) => (params.value === null || params.value === undefined ? '--' : Number(params.value).toFixed(currencyPrecision?.decimalNumbers ?? 2)),
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('amount'),
    },
    {
      headerName: 'Tenant Email Id',
      field: 'tenantEmailId',
      suppressMovable: true,
      sortable: true,
      width: 220,
      cellRenderer: TenantEmailCellRenderer,
      cellRendererParams: { sendOnPrintKeys, onToggle: toggleSendOnPrint },
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('tenantEmailId'),
    },
    {
      headerName: 'Actions',
      field: 'actions',
      suppressMovable: true,
      sortable: false,
      filter: false,
      width: 100,
      cellRenderer: PrintCellRenderer,
      cellRendererParams: { onPrint: printInvoice },
    },
  ];

  return (
    <div className="p-3 installment-list-page">
      <div className="cost-center-section">
        <div className="d-flex mt-3 cursor-pointer slide-header" onClick={() => setFilterTab(!FilterTab)}>
          <div className="h6 pt-1">Search Parameters</div>
        </div>
        <div className={`slide-content ${FilterTab ? 'open' : 'closed'}`}>
          <div className={`border-box2 p-3 ${FilterTab ? 'slide-in' : 'slide-out'}`}>
            <div className="d-flex gap-3 align-items-stretch">
              <div className="form-group grid-contain flex-grow-1">
                <div className="d-flex">
                  <label className="form-label pt-2 start">Property No</label>
                  <Select
                    className="p-0 w-100"
                    theme={selectTheme}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                    value={formData.propertyNo}
                    onChange={handlePropertyChange}
                    options={propertyOptions}
                  />
                </div>
                <div className="d-flex">
                  <label className="form-label pt-2 start">Unit No</label>
                  <Select
                    className="p-0 w-100"
                    theme={selectTheme}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                    value={formData.unitNo}
                    isDisabled={loadingUnits || !formData.propertyNo?.value || formData.propertyNo.value === 'All'}
                    onChange={(selectedOption) => setFormData((prev) => ({ ...prev, unitNo: selectedOption }))}
                    options={unitOptions}
                    isClearable
                  />
                </div>
                <div className="installment-date-row">
                  <div className="d-flex field-date">
                    <label className="form-label pt-2 start">From Date</label>
                    <DatePicker
                      selected={formData.fromDate}
                      onChange={(date) => setFormData((prev) => ({ ...prev, fromDate: date }))}
                      dateFormat="dd-MMM-yyyy"
                      className="p-2 form-control w-100 datepicker-icon"
                    />
                  </div>
                  <div className="d-flex field-date">
                    <label className="form-label pt-2 start">Lease Expiry</label>
                    <DatePicker
                      selected={formData.leaseExpiry}
                      onChange={(date) => setFormData((prev) => ({ ...prev, leaseExpiry: date }))}
                      dateFormat="dd-MMM-yyyy"
                      className="p-2 form-control w-100 datepicker-icon"
                    />
                  </div>
                </div>
              </div>
              <div className="installment-search-actions">
                <div onClick={() => getItems()} className="mainbtn flex-end gap-2">
                  <FaFileCircleExclamation color="#fff" className="center mt-2 mx-2" />
                  <div className="center">Search</div>
                </div>
                <div onClick={() => handleClearFilters()} className="mainbtnRed flex-end gap-2">
                  <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                  <div className="center">Reset</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="border-box2 p-3">
        {loading ? (
          <div>Loading...</div>
        ) : (
          <>
            <div className="d-flex justify-contant-between ">
              <h5 className="col mainInvHead">Installment Invoice List</h5>
              <div className="d-flex justify-content-end gap-1">
                <button className="mainbtn" type="button" onClick={sendSelectedEmails} disabled={selectedRows.length === 0}>
                  <IoMailOutline color="#fff" className="center mt-1" /> <div>Send Email</div>
                </button>
              </div>
            </div>
            <div className="gridshad mt-3">
              <div className="ag-theme-quartz installment-grid" style={{ height: 650 }}>
                <AgGridReact
                  onGridReady={(params) => setGridApi(params.api)}
                  getRowStyle={(params) => {
                    if (params.node.rowIndex % 2 === 0) {
                      return { background: '#F0EDF5' };
                    }
                    return { background: '#fff' };
                  }}
                  rowData={filteredRowData}
                  columnDefs={columnDefs}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  onSelectionChanged={onSelectionChanged}
                  getRowId={(params) => params.data.key}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

class TenantEmailCellRenderer extends Component {
  render() {
    const { data, sendOnPrintKeys, onToggle } = this.props;
    return (
      <div className="installment-tenant-email-cell">
        <input
          type="checkbox"
          checked={sendOnPrintKeys?.has?.(data.key) || false}
          disabled={!data.tenantEmailId}
          title="Send invoice to this email when printed"
          onChange={(e) => onToggle(data.key, e.target.checked)}
        />
        <span>{data.tenantEmailId || '--'}</span>
      </div>
    );
  }
}

class PrintCellRenderer extends Component {
  render() {
    const { data, onPrint } = this.props;
    return (
      <div className="installment-row-actions">
        <FaPrint color="#6E5199" size={16} title="Print Invoice" style={{ cursor: 'pointer' }} onClick={() => onPrint(data)} />
      </div>
    );
  }
}

export default InstallmentInvoiceEmailListing;
